import React from 'react';
import styled from 'styled-components';
import { formatYearDisplay, toTitleCase } from '../../../utils/strings';
import settingsIcon from '../../../static/icons/PurpleSettingIcon.svg';

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  width: 100%;
`;

const BadgesGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const LabeledBadge = styled.div`
  display: inline-flex;
  padding: 6px 12px;
  color: #7200E0;
  font-size: 14px;
  font-weight: 400;
  font-family: 'IBM Plex Sans', sans-serif;
  background: #F9F3FF;
  border: 1px solid var(--color-purple-90, #C596F2);
  border-radius: 6px;
`;

const SettingsButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: none;
  background-color: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-purple-300, #7200E0);
  transition: opacity 0.2s;
  flex-shrink: 0;
  appearance: none;
  -webkit-appearance: none;

  &:hover,
  &:focus,
  &:active {
    background: none;
    background-color: transparent;
    opacity: 0.9;
    cursor: pointer;
  }

  img {
    width: 16px;
    height: 16px;
  }
`;

const SettingsTagsDisplay = ({
  years = [],
  subjects = [],
  taskTypes = [],
  responseTypes = [],
  taskTypeLabels = {},
  responseTypeLabels = {},
  onSettingsClick,
  showSettingsButton = true,
}) => {
  // Use provided labels for mapping codes to display names
  const mergedTaskTypeLabels = taskTypeLabels;
  const mergedResponseTypeLabels = responseTypeLabels;

  const toStr = (v) => (v != null && typeof v === 'object' ? String(v.label ?? v.title ?? v.value ?? '') : String(v));
  const taskTypeDisplay = (t) => {
    const key = t != null && typeof t === 'object' ? t.value : t;
    const raw = mergedTaskTypeLabels[key] ?? (t != null && typeof t === 'object' ? (t.label ?? t.title ?? t.value) : t) ?? '';
    return toTitleCase(String(raw));
  };
  const responseTypeDisplay = (r) => {
    const key = r != null && typeof r === 'object' ? r.value : r;
    const raw = mergedResponseTypeLabels[key] ?? (r != null && typeof r === 'object' ? (r.label ?? r.title ?? r.value) : r) ?? '';
    return toTitleCase(String(raw));
  };

  // Detect duplicate subject titles to show "Name (Code)" only for duplicates
  const subjectTitleCounts = new Map();
  subjects.forEach((s) => {
    const title = s != null && typeof s === 'object'
      ? String(s.title ?? s.subjectName ?? s.name ?? s.label ?? '')
      : String(s ?? '');
    if (title) subjectTitleCounts.set(title, (subjectTitleCounts.get(title) || 0) + 1);
  });

  const subjectDisplay = (s) => {
    let title = '';
    let code = '';
    if (s != null && typeof s === 'object') {
      title = String(s.title ?? s.subjectName ?? s.name ?? s.label ?? '');
      code = String(s.subjectCode ?? s.code ?? s.value ?? '');
      if (!title) title = code;
    } else {
      title = String(s ?? '');
    }
    if (title && title.startsWith('All') && title.length > 3) title = title.slice(3);
    if (title && subjectTitleCounts.get(title) > 1 && code && code !== title) {
      return `${title} (${code})`;
    }
    return title;
  };

  const labeledBadges = [
    years.length > 0 && { label: 'Years', value: years.map((y) => formatYearDisplay(toStr(y))).join(', ') },
    subjects.length > 0 && { label: 'Subjects', value: subjects.map(subjectDisplay).join(', ') },
    taskTypes.length > 0 && { label: 'Task Type', value: taskTypes.map(taskTypeDisplay).join(', ') },
    responseTypes.length > 0 && { label: 'Submission Type', value: responseTypes.map(responseTypeDisplay).join(', ') }
  ].filter(Boolean);

  if (labeledBadges.length === 0 && !showSettingsButton) {
    return null;
  }

  return (
    <Container>
      <BadgesGroup>
        {labeledBadges.map((badge) => (
          <LabeledBadge key={badge.label}>{badge.label}: {badge.value}</LabeledBadge>
        ))}
      </BadgesGroup>
      {showSettingsButton && onSettingsClick && (
        <SettingsButton onClick={onSettingsClick}>
          <img src={settingsIcon} alt="Edit Customisations" />
          Edit Customisations
        </SettingsButton>
      )}
    </Container>
  );
};

export default SettingsTagsDisplay;
